import { remarksSchema } from './common';
import {
  bankAccountHolder,
  fullWidthCharacter,
  halfWidthNumber,
  nameKanaRegex,
  nameRomajiRegex,
  phoneNumberWithHyphenRequired,
  postalCode,
  fullWidthCharacterOnly,
} from './regex';

import {
  BankAccountTypeKey,
  ClearingCycleKey,
} from '@/types';
import {
  InferType,
  boolean,
  date,
  object,
  string,
} from '@/validations/custom-yup';

/** 外部企業ID */
export const externalCompanyIdSchema = string()
  .label('外部企業ID')
  .notRequired()
  .max(100)
  .when(
    'regex',
    ([regex], schema) => (regex ? schema.matches(regex, '外部企業IDのフォーマットを確認してください。') : schema),
  );

/** 企業名 */
export const companyNameSchema = string()
  .label('企業名')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '企業名に使用できない文字が含まれています。');

/** 電話番号 */
export const phoneSchema = string()
  .label('電話番号')
  .required()
  .max(13)
  .matches(phoneNumberWithHyphenRequired, '電話番号を正しい形式で入力してください。');

/** 郵便番号 */
export const postalCodeSchema = string()
  .label('郵便番号')
  .required()
  .matches(postalCode, '郵便番号を正しい形式で入力してください。');

/** 都道府県 */
export const prefectureSchema = string()
  .label('都道府県')
  .required()
  .max(4)
  .matches(fullWidthCharacterOnly, '都道府県に使用できない文字が含まれています。');

/** 市区町村番地 */
export const municipalitiesSchema = string()
  .label('市区町村番地')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '市区町村番地を全角で入力してください。');

/** 建物名 */
export const buildingNameSchema = string()
  .label('建物名')
  .max(100)
  .default('')
  .ensure()
  .matches(fullWidthCharacter, '建物名を全角で入力してください。');

/** 利用開始日 */
export const startsAtSchema = date()
  .label('利用開始日')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

/** 利用終了日 */
export const endsAtSchema = date()
  .label('利用終了日')
  .notRequired()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'startsAt',
    ([startsAt], schema) => (startsAt instanceof Date
      ? schema
        .test(
          'isMoreThanStartsAt',
          '利用終了日は利用開始日以降の日付を入力してください。',
          (value) => (value ? value.getTime() >= startsAt.getTime() : true),
        )
      : schema),
  );

/** 端末絞り表示可否フラグ */
export const terminalOptionalNarrowedFlgSchema = boolean()
  .label('加盟店アプリ端末絞り込み')
  .required(({ label }) => `${label}を選択してください。`);

/** QR法人ID/ブランドID */
export const staticQrCompanyIdSchema = string()
  .label('QR法人ID/ブランドID')
  .notRequired()
  .test(
    'isLength17',
    'QR法人ID/ブランドIDは17文字で入力してください。',
    (value) => (value ? value.length === 17 : true),
  );

/** 金融機関コード */
export const bankCodeSchema = string()
  .label('金融機関コード')
  .length(4, '金融機関コードは4桁で入力してください。')
  .matches(halfWidthNumber, '金融機関コードは半角数字で入力してください。')
  .required();

/** 支店コード */
export const bankBranchCodeSchema = string()
  .label('支店コード')
  .length(3, '支店コードは3桁で入力してください。')
  .matches(halfWidthNumber, '支店コードは半角数字で入力してください。')
  .required();

/** 口座科目 */
export const bankAccountTypeSchema = string<BankAccountTypeKey>()
  .label('口座科目')
  .required(({ label }) => `${label}を選択してください。`);

/** 口座番号 */
export const bankAccountNumberSchema = string()
  .label('口座番号')
  .length(7, '口座番号は7桁で入力してください。')
  .matches(halfWidthNumber, '口座番号は半角数字で入力してください。')
  .required();

/** 口座名義 */
export const bankAccountHolderSchema = string()
  .label('口座名義')
  .max(30)
  .matches(bankAccountHolder, '口座名義に使用できない文字が含まれています。')
  .required();

export const clearingCycleSchema = string()
  .label('精算サイクル');

export const regexSchema = string()
  .label('正規表現');

export const lastUpdatedAtSchema = string()
  .label('最終更新日時')
  .required();

export const bankAccountSchema = object({
  bankCode: bankCodeSchema,
  bankBranchCode: bankBranchCodeSchema,
  bankAccountType: bankAccountTypeSchema,
  bankAccountNumber: bankAccountNumberSchema,
  bankAccountHolder: bankAccountHolderSchema,
})
  .optional()
  .when(
    'clearingCycle',
    ([clearingCycle], schema) => (clearingCycle as ClearingCycleKey === 'NONE' ? schema.default(undefined) : schema.required()),
  );

export const companyNameKanaSchema = string()
  .label('店舗名（カナ）')
  .required()
  .max(64)
  .matches(nameKanaRegex, ({ label }) => `${label}は全角カナ・英数字・一部記号で入力してください。`);

export const companyNameRomajiSchema = string()
  .label('店舗名（ローマ字）')
  .required()
  .max(32)
  .matches(nameRomajiRegex, ({ label }) => `${label}は半角英数字・記号（「"」「,」を除く）で入力してください。`);

export const companyMerchantCategoryCodeSchema = string()
  .label('業種コード')
  .required()
  .length(5)
  .matches(halfWidthNumber, ({ label }) => `${label}は半角数字で入力してください。`);

export const companyUpdateSmartCodeInfoSchema = object({
  nameKana: companyNameKanaSchema,
  nameRomaji: companyNameRomajiSchema,
  merchantCategoryCode: companyMerchantCategoryCodeSchema,
});

export type CompanyUpdateSmartCodeInfoSchema = InferType<typeof companyUpdateSmartCodeInfoSchema>;

export const companyEditFormSchema = object({
  externalCompanyId: externalCompanyIdSchema,
  companyName: companyNameSchema,
  phone: phoneSchema,
  postalCode: postalCodeSchema,
  prefecture: prefectureSchema,
  municipalities: municipalitiesSchema,
  buildingName: buildingNameSchema,
  startsAt: startsAtSchema,
  endsAt: endsAtSchema,
  terminalOptionalNarrowedFlg: terminalOptionalNarrowedFlgSchema,
  staticQrCompanyId: staticQrCompanyIdSchema,
  bankAccount: bankAccountSchema,
  clearingCycle: clearingCycleSchema,
  remarks: remarksSchema(),
  regex: regexSchema,
  lastUpdatedAt: lastUpdatedAtSchema,
  companyUpdateSmartCodeInfo: companyUpdateSmartCodeInfoSchema.notRequired(),
});

export type CompanyEditForm = InferType<typeof companyEditFormSchema>;
